import { Box, GridItem } from '@chakra-ui/react';
import { FC, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';

import { FieldComponentProps } from '../types';
import { FormContext } from './form';
import { FormItemProps } from './types';
import { getFieldValue } from './utils';

export function FormItem<T, V = any>(props: FormItemProps<T>) {
  const {
    label,
    field,
    span = { md: 6 },
    validation,
    required,
    container,
    readOnly = false,
    component,
    componentProps,
    containerProps,
  } = props;

  const { instance, errors, onChange, addValidation, addRequired } = useContext(FormContext);

  useEffect(() => {
    if(!validation) {
      return;
    }
    addValidation(field, label, validation);
  }, [validation, field, label]);

  useEffect(() => {
    if(!required) {
      return;
    }
    addRequired(field, label);
  }, [required, field, label]);

  const value: V = getFieldValue(instance, field);

  const Component = component as FC<FieldComponentProps<V>>;
  const Container = container as FC<FieldComponentProps<V> & PropsWithChildren>;
  
  const [containerAdvProps] = useState(containerProps);

  const changeHandler = useCallback((value: V) => {
    if (readOnly) {return;}
    onChange(field)(value);
    props.onChange?.(value);
  }, [onChange, field, props]);

  if (Container) {
    return <GridItem colSpan={{ ...span }} >
      <Container 
        id={field.toString()}
        label={label}
        required={required}
        value={value}
        onChange={changeHandler}
        error={!!errors[field.toString()]}
        helperText={errors[field.toString()]}
        {...containerAdvProps}
      >
        <Component 
          id={field.toString()}
          label={label}
          required={required}
          value={value}
          onChange={changeHandler}
          error={!!errors[field.toString()]}
          helperText={errors[field.toString()]}
          {...componentProps}
          readOnly={readOnly}
        />        
      </Container>
    {/* {readOnly && <Box sx={readOnlyStyle} />}  */}
    </GridItem>;
  }

  return <GridItem colSpan={{ ...span }} >
    <Component 
      id={field.toString()}
      label={label}
      required={required}
      value={value}
      onChange={changeHandler}
      error={!!errors[field.toString()]}
      helperText={errors[field.toString()]}
      {...componentProps}
      readOnly={readOnly}
    />
    {/* {readOnly && <Box sx={readOnlyStyle} />}  */}
  </GridItem>;
}

export function FormOutletItem<T, V = any>(props: Omit<FormItemProps<T>, 'span'>) {
  const {
    label,
    field,
    validation,
    required,
    readOnly = false,
    container,
    component,
    componentProps,
    containerProps,
  } = props;

  const { instance, errors, onChange, addValidation, addRequired } = useContext(FormContext);

  useEffect(() => {
    if(!validation) {
      return;
    }
    addValidation(field, label, validation);
  }, [validation, field, label]);

  useEffect(() => {
    if(!required) {
      return;
    }
    addRequired(field, label);
  }, [required, field, label]);

  const value: V = getFieldValue(instance, field);

  const Component = component as FC<FieldComponentProps<V>>;
  const Container = container as FC<FieldComponentProps<V> & PropsWithChildren>;
  
  const [containerAdvProps] = useState(containerProps);

  const changeHandler = useCallback((value: V) => {
    if (readOnly) {return;}
    onChange(field)(value);
    props.onChange?.(value);
  }, [onChange, field, props.onChange]);

  if (Container) {
    return <>
      <Container 
        id={field.toString()}
        label={label}
        required={required}
        value={value}
        onChange={changeHandler}
        error={!!errors[field.toString()]}
        helperText={errors[field.toString()]}
        {...containerAdvProps}
      >
        <Component 
          id={field.toString()}
          label={label}
          required={required}
          value={value}
          error={!!errors[field.toString()]}
          helperText={errors[field.toString()]}
          onChange={changeHandler}
          {...componentProps}
          readOnly={readOnly}
        />
      </Container>
    {/* {readOnly && <Box sx={readOnlyStyle} />}  */}
    </>;
  }

  return <>
    <Component 
      id={field.toString()}
      label={label}
      required={required}
      value={value}
      onChange={changeHandler}
      error={!!errors[field.toString()]}
      helperText={errors[field.toString()]}
      {...componentProps}
      readOnly={readOnly}
    />
    {/* {readOnly && <Box sx={readOnlyStyle} />}   */}
  </>;
}

const readOnlyStyle = {
  position: 'absolute',
  zIndex: 100,
  right: 0,
  top: 0,
  bottom: -1000,
  left: 0,
  backgroundColor: 'rgba(0, 0, 0, 0)',
};